import { useState, useEffect, useContext, createContext } from 'react';
import { useRouter } from 'next/navigation';

// Create Auth Context
const AuthContext = createContext();

// Auth Provider Component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const router = useRouter();

  // Check for user on initial load
  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');

    if (token && userData) {
      try {
        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);
      } catch (error) {
        console.error('Error parsing user data:', error);
        // Clear invalid data
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }
    setLoading(false);
  }, []);

  // Enhanced authentication check
  const checkAuth = () => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (!token || !userData) {
      return { isAuthenticated: false, user: null };
    }
    
    try {
      const parsedUser = JSON.parse(userData);
      return { 
        isAuthenticated: true, 
        user: parsedUser,
        hasRole: (role) => parsedUser?.role === role,
        hasAnyRole: (roles) => roles.includes(parsedUser?.role)
      };
    } catch (error) {
      console.error('Error parsing user data:', error);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      return { isAuthenticated: false, user: null };
    }
  };

  // Role-based access control
  const hasRole = (role) => {
    return user?.role === role;
  };

  const hasAnyRole = (roles) => {
    return roles.includes(user?.role);
  };

  // Check if user can access specific route
  const canAccessRoute = (route) => {
    if (!user) return false;
    
    // Admin routes
    const adminRoutes = [
      '/admin/',
      '/system-settings',
      '/users',
      '/providers',
      '/buses',
      '/bookings',
      '/support-tickets'
    ];
    
    // Customer routes
    const customerRoutes = [
      '/customer/',
      '/booking',
      '/my-bookings',
      '/profile'
    ];
    
    // Provider routes
    const providerRoutes = [
      '/provider/',
      '/provider/buses',
      '/provider/bookings'
    ];
    
    // Check route access based on user role
    if (adminRoutes.some(adminRoute => route.startsWith(adminRoute))) {
      return user.role === 'admin';
    }
    
    if (customerRoutes.some(customerRoute => route.startsWith(customerRoute))) {
      return user.role === 'customer';
    }
    
    if (providerRoutes.some(providerRoute => route.startsWith(providerRoute))) {
      return user.role === 'provider';
    }
    
    // Default: allow access to common routes
    return true;
  };

  // Navigate to appropriate dashboard based on role
  const navigateToDashboard = () => {
    if (!user) {
      router.push('/login');
      return;
    }
    
    const dashboardRoutes = {
      'admin': '/admin/dashboard',
      'customer': '/customer/dashboard',
      'provider': '/provider/dashboard',
      'counter_manager': '/counter/dashboard',
      'support': '/support/dashboard',
      'customer_care': '/customer-care/dashboard'
    };
    
    const route = dashboardRoutes[user.role] || '/dashboard';
    router.push(route);
  };

  // Check if user account is active
  const isAccountActive = () => {
    return user?.isActive !== false;
  };

  // Login function
  const login = (userData, token) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  // Logout function
  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('selectedSeats');
    localStorage.removeItem('selectedBusId');
    localStorage.removeItem('selectedTravelDate');
    localStorage.removeItem('selectedScheduleId');
    localStorage.removeItem('selectedScheduleFare');
    localStorage.removeItem('selectedBoardingPlaces');
    localStorage.removeItem('whatsappPopupVisible');
    setUser(null);
  };

  // Refresh user data
  const refreshUser = (userData) => {
    if (userData) {
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
    }
  };

  const value = {
    user,
    loading,
    login,
    logout,
    refreshUser,
    checkAuth,
    hasRole,
    hasAnyRole,
    canAccessRoute,
    navigateToDashboard,
    isAccountActive,
    isAuthenticated: !!user && isAccountActive()
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Enhanced route protection hook
export const useRouteProtection = (requiredRole = null) => {
  const { user, isAuthenticated, loading, hasRole, canAccessRoute, isAccountActive } = useAuth();
  const router = useRouter();
  
  useEffect(() => {
    if (loading) return;
    
    // Check authentication
    if (!isAuthenticated || !user) {
      console.warn('Route protection: User not authenticated');
      router.push('/login');
      return;
    }
    
    // Check account status
    if (!isAccountActive()) {
      console.warn('Route protection: Account deactivated');
      router.push('/account-deactivated');
      return;
    }
    
    // Check role requirements
    if (requiredRole && !hasRole(requiredRole)) {
      console.warn(`Route protection: Role ${requiredRole} required, user has ${user.role}`);
      
      // Redirect to appropriate dashboard
      const dashboardRoutes = {
        'admin': '/admin/dashboard',
        'customer': '/customer/dashboard',
        'provider': '/provider/dashboard',
        'counter_manager': '/counter/dashboard',
        'support': '/support/dashboard',
        'customer_care': '/customer-care/dashboard'
      };
      
      const redirectRoute = dashboardRoutes[user.role] || '/unauthorized';
      router.push(redirectRoute);
      return;
    }
    
    console.log(`Route protection: Access granted for ${user.role} user ${user.id}`);
    
  }, [user, isAuthenticated, loading, requiredRole, router, hasRole, isAccountActive]);
  
  return { loading, user, isAuthenticated };
};

// Higher Order Component for protecting routes
export const withAuth = (WrappedComponent, requiredRole = null) => {
  return (props) => {
    const { user, loading, isAuthenticated, hasRole, isAccountActive } = useAuth();
    const router = useRouter();

    useEffect(() => {
      if (loading) return;
      
      if (!isAuthenticated || !user) {
        router.push('/login');
        return;
      }
      
      if (!isAccountActive()) {
        router.push('/account-deactivated');
        return;
      }
      
      if (requiredRole && !hasRole(requiredRole)) {
        const dashboardRoutes = {
          'admin': '/admin/dashboard',
          'customer': '/customer/dashboard',
          'provider': '/provider/dashboard',
          'counter_manager': '/counter/dashboard',
          'support': '/support/dashboard',
          'customer_care': '/customer-care/dashboard'
        };
        
        const redirectRoute = dashboardRoutes[user.role] || '/unauthorized';
        router.push(redirectRoute);
        return;
      }
    }, [user, isAuthenticated, loading, router, hasRole, isAccountActive, requiredRole]);

    if (loading) {
      return (
        <div className="flex justify-center items-center h-screen">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-green-600"></div>
        </div>
      );
    }

    if (!isAuthenticated || (requiredRole && !hasRole(requiredRole))) {
      return null;
    }

    return <WrappedComponent {...props} />;
  };
};